import React, { useState } from 'react';
import useWindowWidth from '../../hooks/useWindowWidth';

const styles = {
  body: { padding: '16px' },
  modeSwitcher: {
    display: 'flex', gap: '4px', marginBottom: '16px',
    backgroundColor: '#f3f4f6', borderRadius: '8px', padding: '4px',
  },
  modeBtn: {
    flex: 1, padding: '6px 12px', borderRadius: '6px', cursor: 'pointer',
    textAlign: 'center', fontSize: '12px', fontWeight: '600',
    border: 'none', backgroundColor: 'transparent', color: '#6b7280',
    fontFamily: 'inherit',
  },
  modeBtnActive: { backgroundColor: '#fff', color: '#0052CC', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  proBadge: {
    fontSize: '10px', fontWeight: '700', padding: '1px 6px',
    borderRadius: '10px', backgroundColor: '#dbeafe', color: '#0052CC',
    marginLeft: '6px',
  },
  categoryTab: {
    display: 'flex', gap: '4px', marginBottom: '16px',
    borderBottom: '2px solid #e5e7eb', overflowX: 'auto',
  },
  tab: {
    padding: '7px 14px', fontSize: '12px', fontWeight: '600',
    cursor: 'pointer', border: 'none', background: 'none',
    color: '#6b7280', borderBottom: '2px solid transparent',
    marginBottom: '-2px', whiteSpace: 'nowrap', fontFamily: 'inherit',
    flexShrink: 0,
  },
  tabActive: { color: '#0052CC', borderBottom: '2px solid #0052CC' },
  tabBadge: {
    marginLeft: '5px', fontSize: '10px', fontWeight: '700',
    padding: '1px 5px', borderRadius: '10px',
    backgroundColor: '#e5e7eb', color: '#374151',
  },
  tabBadgeActive: { backgroundColor: '#0052CC', color: '#fff' },
  paramBlock: { marginBottom: '20px' },
  paramLabel: { fontSize: '13px', fontWeight: '700', color: '#111827', marginBottom: '2px', display: 'block' },
  paramDesc: { fontSize: '11px', color: '#6b7280', marginBottom: '8px' },
  sliderRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  slider: { flex: 1, accentColor: '#0052CC', cursor: 'pointer' },
  numInput: {
    width: '70px', padding: '4px 8px', borderRadius: '6px', flexShrink: 0,
    border: '1px solid #d1d5db', fontSize: '12px', textAlign: 'center',
    outline: 'none', fontFamily: 'inherit',
  },
  numInputActive: { border: '1px solid #0052CC', backgroundColor: '#EBF5FF' },
  rangeLabel: { fontSize: '10px', color: '#9ca3af', display: 'flex', justifyContent: 'space-between', marginTop: '2px' },
  unit: { fontSize: '11px', color: '#6b7280', flexShrink: 0, minWidth: '30px' },
  resetBtn: {
    fontSize: '11px', color: '#6b7280', cursor: 'pointer', background: 'none',
    border: 'none', textDecoration: 'underline', padding: 0, fontFamily: 'inherit',
  },
  paramHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' },
  disclaimer: {
    fontSize: '11px', color: '#6b7280', backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb', borderRadius: '6px',
    padding: '8px 10px', marginBottom: '14px', lineHeight: '1.5',
  },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '16px',
  },
};

// Parameter definitions with ranges, units, defaults
const PARAMETRIC_CATEGORIES = [
  {
    id: 'motor_speech',
    label: 'Motor Speech',
    params: [
      {
        id: 'amplitude_modulation',
        label: 'Amplitude Modulation',
        desc: 'Sinusoidal amplitude modulation (tremolo)',
        fields: [
          { key: 'tremolo_freq', label: 'Frequency', unit: 'Hz', min: 0, max: 10, step: 0.1, default: 0 },
          { key: 'tremolo_depth', label: 'Depth', unit: '0–1', min: 0, max: 1, step: 0.01, default: 0 },
        ],
      },
      {
        id: 'pitch_modulation',
        label: 'Pitch Modulation',
        desc: 'Sinusoidal frequency modulation (vibrato)',
        fields: [
          { key: 'vibrato_freq', label: 'Frequency', unit: 'Hz', min: 0, max: 10, step: 0.1, default: 0 },
          { key: 'vibrato_depth', label: 'Depth', unit: '0–1', min: 0, max: 1, step: 0.01, default: 0 },
        ],
      },
      {
        id: 'spectral_muffling',
        label: 'Spectral Muffling',
        desc: 'Low-pass filter cutoff frequency',
        fields: [
          { key: 'lowpass_freq', label: 'Cutoff', unit: 'Hz', min: 0, max: 8000, step: 100, default: 0, zeroLabel: 'Off' },
        ],
      },
      {
        id: 'harmonic_compression',
        label: 'Harmonic Compression',
        desc: 'Dynamic range compression',
        fields: [
          { key: 'compressor_threshold', label: 'Threshold', unit: 'dB', min: -30, max: 0, step: 1, default: 0 },
          { key: 'compressor_ratio', label: 'Ratio', unit: ':1', min: 1, max: 12, step: 0.5, default: 1 },
        ],
      },
      {
        id: 'piecewise_rate',
        label: 'Piecewise Rate Variation',
        desc: 'Segment-level tempo perturbation range',
        fields: [
          { key: 'tempo_min', label: 'Tempo Min', unit: '×', min: 0.7, max: 1.0, step: 0.01, default: 1.0 },
          { key: 'tempo_max', label: 'Tempo Max', unit: '×', min: 1.0, max: 1.35, step: 0.01, default: 1.0 },
          { key: 'max_step', label: 'Max Step', unit: '×', min: 0.05, max: 0.20, step: 0.01, default: 0.12 },
        ],
      },
    ],
  },
  {
    id: 'voice',
    label: 'Voice',
    params: [
      {
        id: 'breathiness',
        label: 'Spectral Tilt + Aspiration',
        desc: 'High-shelf attenuation with envelope-following high-passed noise',
        fields: [
          { key: 'tilt_freq', label: 'Shelf Freq', unit: 'Hz', min: 1000, max: 5000, step: 100, default: 2000 },
          { key: 'tilt_gain', label: 'Shelf Gain', unit: 'dB', min: -15, max: 0, step: 0.5, default: 0 },
          { key: 'aspiration_level', label: 'Aspiration Noise', unit: '0–0.5', min: 0, max: 0.5, step: 0.01, default: 0 },
        ],
      },
      {
        id: 'reduced_loudness',
        label: 'Reduced Loudness',
        desc: 'Linear amplitude multiplier',
        fields: [
          { key: 'volume', label: 'Volume', unit: '×', min: 0.2, max: 1.0, step: 0.01, default: 1.0 },
        ],
      },
      {
        id: 'resonance_boost',
        label: 'Low-Freq Resonance Boost',
        desc: 'Multi-band nasal resonance approximation',
        fields: [
          { key: 'nasal_boost_freq', label: 'Center Freq', unit: 'Hz', min: 250, max: 500, step: 10, default: 350 },
          { key: 'nasal_boost_gain', label: 'Gain', unit: 'dB', min: 0, max: 12, step: 0.5, default: 0 },
          { key: 'nasal_boost_width', label: 'Width', unit: 'Hz', min: 100, max: 500, step: 10, default: 200 },
          { key: 'oral_cut_gain', label: 'Oral Cut', unit: 'dB', min: -8, max: 0, step: 0.5, default: 0 },
        ],
      },
      {
        id: 'voice_stability',
        label: 'Voice Stability',
        desc: 'ElevenLabs synthesis-level parameters',
        fields: [
          { key: 'stability', label: 'Stability', unit: '0–1', min: 0.1, max: 1.0, step: 0.01, default: 0.75 },
          { key: 'similarity_boost', label: 'Similarity', unit: '0–1', min: 0.1, max: 1.0, step: 0.01, default: 0.75 },
          { key: 'style', label: 'Style', unit: '0–1', min: 0, max: 1.0, step: 0.01, default: 0 },
        ],
      },
    ],
  },
  {
    id: 'fluency',
    label: 'Fluency & Rate',
    params: [
      {
        id: 'global_rate',
        label: 'Global Speech Rate',
        desc: 'TTS synthesis-level speed (ElevenLabs)',
        fields: [
          { key: 'speed', label: 'Speed', unit: '×', min: 0.7, max: 1.2, step: 0.01, default: 1.0 },
        ],
      },
      {
        id: 'pause_length',
        label: 'Inter-Phrase Pause',
        desc: 'Ellipsis injection duration at clause boundaries',
        fields: [
          { key: 'pause_ms', label: 'Duration', unit: 'ms', min: 0, max: 2000, step: 50, default: 0 },
        ],
      },
    ],
  },
  {
    id: 'environment',
    label: 'Environment',
    params: [
      {
        id: 'background_noise',
        label: 'Background Noise',
        desc: 'SNR-based noise mixing (anoisesrc + amix)',
        fields: [
          { key: 'snr_db', label: 'SNR', unit: 'dB', min: 5, max: 35, step: 1, default: 35, zeroLabel: 'Off (35 dB)' },
        ],
        extras: [
          { key: 'noise_color', label: 'Noise Color', type: 'select', options: ['white', 'pink', 'brown'], default: 'pink' },
        ],
      },
      {
        id: 'channel_bandwidth',
        label: 'Channel Bandwidth',
        desc: 'Bandpass filtering for transmission degradation',
        fields: [
          { key: 'channel_highpass_freq', label: 'High-Pass', unit: 'Hz', min: 0, max: 500, step: 10, default: 0 },
          { key: 'channel_lowpass_freq', label: 'Low-Pass', unit: 'Hz', min: 2000, max: 20000, step: 100, default: 20000 },
        ],
      },
    ],
  },
];

// Build flat default values
function buildDefaults() {
  const defaults = {};
  PARAMETRIC_CATEGORIES.forEach(cat => {
    cat.params.forEach(param => {
      param.fields.forEach(f => { defaults[f.key] = f.default; });
      (param.extras || []).forEach(e => { defaults[e.key] = e.default; });
    });
  });
  return defaults;
}

const PARAMETRIC_DEFAULTS = buildDefaults();

function isParamActive(param, values) {
  return param.fields.some(f => {
    const v = values[f.key];
    return v !== undefined && v !== f.default;
  });
}

function countActiveParams(values) {
  return PARAMETRIC_CATEGORIES.reduce((total, cat) => {
    return total + cat.params.filter(p => isParamActive(p, values)).length;
  }, 0);
}

function SliderField({ field, value, onChange, isMobile }) {
  const [inputVal, setInputVal] = useState(String(value));
  const isActive = value !== field.default;

  // Keep inputVal in sync when value changes externally (e.g. slider)
  React.useEffect(() => {
    setInputVal(String(value));
  }, [value]);

  const handleSlider = (e) => {
    const v = parseFloat(e.target.value);
    onChange(field.key, v);
    setInputVal(String(v));
  };

  const handleInput = (e) => {
    setInputVal(e.target.value); // allow free typing
  };

  const handleInputBlur = () => {
    const v = parseFloat(inputVal);
    if (!isNaN(v)) {
      const clamped = Math.max(field.min, Math.min(field.max, v));
      onChange(field.key, clamped);
      setInputVal(String(clamped));
    } else {
      setInputVal(String(value)); // revert to last valid value
    }
  };

  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={styles.paramHeader}>
        <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>{field.label}</span>
        <button style={styles.resetBtn} onClick={() => {
          onChange(field.key, field.default);
          setInputVal(String(field.default));
        }}>reset</button>
      </div>
      <div style={{
        ...styles.sliderRow,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '6px' : '10px',
      }}>
        <input
          type="range"
          style={{ ...styles.slider, width: '100%' }}
          min={field.min} max={field.max} step={field.step}
          value={value}
          onChange={handleSlider}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <input
            type="number"
            style={{
              ...styles.numInput,
              ...(isActive ? styles.numInputActive : {}),
              width: isMobile ? '80px' : '70px',
            }}
            min={field.min} max={field.max} step={field.step}
            value={inputVal}
            onChange={handleInput}
            onBlur={handleInputBlur}
          />
          <span style={styles.unit}>{field.unit}</span>
        </div>
      </div>
      <div style={styles.rangeLabel}>
        <span>{field.min}{field.unit === '0–1' ? '' : ' ' + field.unit}</span>
        <span>{field.max}{field.unit === '0–1' ? '' : ' ' + field.unit}</span>
      </div>
    </div>
  );
}

export default function ParametricPanel({ values, onChange }) {
  const [activeTab, setActiveTab] = useState('motor_speech');
  const width = useWindowWidth();
  const isMobile = width < 768;
  const merged = { ...PARAMETRIC_DEFAULTS, ...values };
  const activeCount = countActiveParams(merged);

  function handleChange(key, val) {
    onChange({ ...merged, [key]: val });
  }

  function resetParam(param) {
    const reset = { ...merged };
    param.fields.forEach(f => { reset[f.key] = f.default; });
    (param.extras || []).forEach(e => { reset[e.key] = e.default; });
    onChange(reset);
  }

  return (
    <div>
      <div style={styles.disclaimer}>
        ⚠️ Parametric controls set exact acoustic signal values. Parameters are not clinically validated disorder simulations. Values are logged with simulation metadata for methods reporting.
      </div>

      {/* Category tabs */}
      <div style={styles.categoryTab}>
        {PARAMETRIC_CATEGORIES.map(cat => {
          const catActive = cat.params.filter(p => isParamActive(p, merged)).length;
          const isTabActive = activeTab === cat.id;
          return (
            <button
              key={cat.id}
              style={{ ...styles.tab, ...(isTabActive ? styles.tabActive : {}) }}
              onClick={() => setActiveTab(cat.id)}
            >
              {cat.label}
              {catActive > 0 && (
                <span style={{ ...styles.tabBadge, ...(isTabActive ? styles.tabBadgeActive : {}) }}>
                  {catActive}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Parameters */}
      {PARAMETRIC_CATEGORIES.filter(c => c.id === activeTab).map(cat => (
        <div key={cat.id} style={styles.grid}>
          {cat.params.map(param => {
            const active = isParamActive(param, merged);
            return (
              <div key={param.id} style={{
                ...styles.paramBlock,
                padding: '14px', borderRadius: '8px',
                border: `1px solid ${active ? '#bfdbfe' : '#e5e7eb'}`,
                backgroundColor: active ? '#f0f7ff' : '#fff',
              }}>
                <div style={styles.paramHeader}>
                  <div>
                    <label style={styles.paramLabel}>{param.label}</label>
                    <div style={styles.paramDesc}>{param.desc}</div>
                  </div>
                  {active && (
                    <button style={styles.resetBtn} onClick={() => resetParam(param)}>reset all</button>
                  )}
                </div>
                {param.fields.map(field => (
                  <SliderField
                    key={field.key}
                    field={field}
                    value={merged[field.key] ?? field.default}
                    onChange={handleChange}
                    isMobile={isMobile}
                  />
                ))}
                {(param.extras || []).map(extra => (
                  <div key={extra.key} style={{ marginTop: '8px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                      {extra.label}
                    </label>
                    <select
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', fontFamily: 'inherit' }}
                      value={merged[extra.key] ?? extra.default}
                      onChange={e => handleChange(extra.key, e.target.value)}
                    >
                      {extra.options.map(o => (
                        <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export { PARAMETRIC_DEFAULTS, PARAMETRIC_CATEGORIES };
